import { registerPlugin } from '@capacitor/core';
import { readGpsTrackPoints, type GpsStoredPoint } from './karta/gpsUtils';
import { drainBackgroundTrack } from './backgroundTrack';

/**
 * Jägarhälsa: dagens steg ur Hälsa på iPhone (telefonens och klockans steg sammanslagna av Hälsa).
 * Pulsen kommer från klockan via watchBridge.ts. Nativ del: ios/App/App/HunterHealthPlugin.swift.
 */

const HunterHealth = registerPlugin<{
  stepsToday(): Promise<{ steps: number }>;
  beginHuntWorkout(options: { meetingId: string }): Promise<{ authorized: boolean }>;
  finishHuntWorkout(options: { meetingId: string; watchInHunt: boolean; points: GpsStoredPoint[] }): Promise<{ saved: boolean; reason: string }>;
}>('HunterHealth');

export function isHunterHealthAvailable(): boolean {
  const cap = (window as any).Capacitor;
  return !!cap?.isNativePlatform?.() && cap.getPlatform?.() === 'ios' && !!cap.isPluginAvailable?.('HunterHealth');
}

/** Dagens steg, eller null om Hälsa saknas eller behörighet nekats. Första anropet frågar om lov. */
export async function stepsToday(): Promise<number | null> {
  if (!isHunterHealthAvailable()) return null;
  try {
    return (await HunterHealth.stepsToday()).steps;
  } catch {
    return null;
  }
}

/**
 * Jakten som träningspass i Hälsa. Regeln mot dubbletter: var min egen Apple Watch med i jakten
 * (pulsen kom från klockans pass, eller servern såg klockan) sparar klockan passet och Hälsa synkar
 * det hit — telefonen sparar inget. Annars sparar telefonen passet med tid och rutt från spåret.
 * Pluginet kollar dessutom att inget jaktpass redan finns i Hälsa för samma tid.
 */
const WATCH_IN_HUNT_KEY = 'qvenari_hunt_watch_seen';

/** Jakten startade på telefonen (eller visas igen) — starttiden sparas i appen. */
export function beginHuntWorkout(meetingId: string): void {
  if (!isHunterHealthAvailable()) return;
  HunterHealth.beginHuntWorkout({ meetingId }).catch(() => {});
}

/** Min klocka är med i jakten. Sparas så att det gäller även om kartan laddas om under jakten. */
export function markWatchInHunt(meetingId: string): void {
  try { localStorage.setItem(WATCH_IN_HUNT_KEY, meetingId); } catch { /* ignore */ }
}

export function watchWasInHunt(meetingId: string): boolean {
  try { return localStorage.getItem(WATCH_IN_HUNT_KEY) === meetingId; } catch { return false; }
}

/**
 * Jakten är slut för telefonens del (avslutad, lämnad eller stängd av servern): spara passet om ingen
 * klocka var med. Rutten är webbvyns spår plus punkterna som togs med skärmen låst. Går att anropa
 * flera gånger för samma jakt — bara det första anropet kan spara.
 */
export async function finishHuntWorkout(meetingId: string): Promise<void> {
  const points = mergeTrackPoints(readGpsTrackPoints(), await drainBackgroundTrack());
  // Märket lämnas kvar: ett andra anrop för samma jakt ska också se klockan. Nästa jakt har ett annat id.
  const watchInHunt = watchWasInHunt(meetingId);
  if (!isHunterHealthAvailable()) return;
  try {
    const r = await HunterHealth.finishHuntWorkout({ meetingId, watchInHunt, points });
    console.log('[HunterHealth] träningspass', r);
  } catch (e) {
    // Äldre appbygge utan metoden, eller Hälsa saknas.
    console.warn('[HunterHealth] träningspasset sparades inte', e);
  }
}

/** Båda spåren i tidsordning, utan dubbletter. */
export function mergeTrackPoints(a: GpsStoredPoint[], b: GpsStoredPoint[]): GpsStoredPoint[] {
  const byTime = new Map<number, GpsStoredPoint>();
  for (const p of [...a, ...b]) byTime.set(p.t, p);
  return [...byTime.values()].sort((x, y) => x.t - y.t);
}
